/* eslint-disable no-param-reassign */
import React, { useEffect } from 'react'

import { useInView } from 'react-cool-inview'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  func, string, bool, number, object,
} from 'prop-types'

import WithTriangleUiContext from 'Pods/Shared/Hocs/WithTriangleUiContext'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'


// Conservative default for the narrow layouts (carousels, sidebars, 2-4 column grids) that render
// this card. A list whose above-the-fold row is wider raises it explicitly.
const DEFAULT_EAGER_IMAGE_COUNT = 2

function ModelCardImage({
  id,
  index,
  eagerImageCount = DEFAULT_EAGER_IMAGE_COUNT,
  isLoading,
  image = EMPTY_OBJECT,
  previewCount = '',
  onPreviewClick = null,
  onImageClick = null,
  productUrl,
  className = '',
  onAuxImageClick = null,
  isTitleVisible = false,
  isSkeletonComponent = false,
  sizes,
  isTrackingInfoAvailable,
  areTrackingUtilsAvailable,
  shouldTrackMobileTreshold,
  trackLastItemInView,
  ...rest
}) {
  const isImportantImage = index <= 1
  const isAboveTheFold = index < eagerImageCount
  // Only the first two images get `high` so the priority hint stays meaningful; the rest of
  // the first row loads eagerly at browser-default priority, everything below stays `low`.
  let fetchPriority = 'low'

  if (isImportantImage) {
    fetchPriority = 'high'
  } else if (isAboveTheFold) {
    fetchPriority = 'auto'
  }
  const { gridUrl, gridFallbackUrl, gridCandidates = EMPTY_ARRAY } = image || {}
  // Only variants already rendered in S3 arrive as candidates; a missing one would be processed
  // synchronously on the web tier at request time.
  const hasWidthCandidates = gridCandidates.length > 0
  const webpSrcSet = hasWidthCandidates
    ? gridCandidates.map(({ url, width }) => `${url} ${width}w`).join(', ')
    : gridUrl

  const { observe, inView } = useInView({
    threshold: 0.7, // only count 70% visible item for impression.
    onEnter: ({ unobserve }) => {
      // Skip during auto-scroll - don't unobserve so it can fire again when user manually scrolls
      if (typeof window !== 'undefined' && window.isAutoScrolling) {
        return
      }

      unobserve() // unobserve to avoid multiple impressions on same item.
      handleImpression()

      if (shouldTrackMobileTreshold) {
        trackLastItemInView()
      }
    },
  })

  useEffect(() => {
    if (isTrackingInfoAvailable && areTrackingUtilsAvailable && inView) {
      if (typeof window !== 'undefined' && window.initItems) {
        window.initItems()
      }

      handleImpression()
    }
  }, [isTrackingInfoAvailable, areTrackingUtilsAvailable, inView])

  const handleImpression = () => {
    if (inView && typeof window !== 'undefined' && window.handleImpression) {
      window.handleImpression(Number(id))
    }
  }

  const pictureComponent = (
    <picture>
      {webpSrcSet && (
        <source srcSet={webpSrcSet} sizes={hasWidthCandidates ? sizes : undefined} type="image/webp" />
      )}
      {gridFallbackUrl && <source srcSet={gridFallbackUrl} type="image/jpeg" />}
      <img
        alt="Start Selling on CGTrader Today"
        className={classnames(
          'cgt-model-card__image',
          isLoading && 'shimmer',
          !!onImageClick && !isSkeletonComponent && 'cgt-model-card__image--clickable',
          isTitleVisible && 'cgt-model-card__image--with-title',
        )}
        src={gridFallbackUrl || gridUrl}
        loading={isAboveTheFold ? 'eager' : 'lazy'}
        fetchpriority={fetchPriority}
        {...rest}
      />
    </picture>
  )

  function renderImage() {
    if (isSkeletonComponent) {
      return (
        <div className="cgt-model-card__link">
          {pictureComponent}
        </div>
      )
    }

    if (image.gridUrl || image.gridFallbackUrl) {
      return (
        <a
          href={productUrl}
          onClick={onImageClick}
          onAuxClick={onAuxImageClick}
          className="cgt-model-card__link js-track-click"
          data-attributes={JSON.stringify({ id, item_index: index })}
        >
          {pictureComponent}
        </a>
      )
    }

    return null
  }

  return (
    <div
      className={classnames(
        'cgt-model-card__image-container card-3d-model__image-container',
        isLoading && 'card-3d-model__image-container--loading',
      )}
      ref={observe}
    >
      {renderImage()}
    </div>
  )
}

ModelCardImage.propTypes = {
  isLoading: bool.isRequired,
  image: object,
  previewCount: string,
  onPreviewClick: func,
  onImageClick: func,
  productUrl: string,
  className: string,
  id: string.isRequired,
  index: number.isRequired,
  eagerImageCount: number,
  onAuxImageClick: func,
  isTitleVisible: bool,
  isSkeletonComponent: bool,
  sizes: string,
  isTrackingInfoAvailable: bool.isRequired,
  areTrackingUtilsAvailable: bool.isRequired,
  shouldTrackMobileTreshold: bool.isRequired,
  trackLastItemInView: func.isRequired,
}

const mapStateToProps = ({
  isLoading,
  isTrackingInfoAvailable,
  areTrackingUtilsAvailable,
}) => ({
  isLoading,
  isTrackingInfoAvailable,
  areTrackingUtilsAvailable,
})

export default WithTriangleUiContext(mapStateToProps)(ModelCardImage)
